import React, { Component, ReactNode } from 'react';
import Icon from '../../icons/Icon';
import { DeploymentStatus } from '../../constant/Constants';
import ItemStatusComponent from '../ItemStatusComponent';
import ComponentName from '../../constant/ComponentName';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { RootState } from '../../redux/store';
import { ComponentState } from '../../redux/actions/ComponentActions';
import { query, repository, subscribe } from '@trlogic/client-js';
import CollectionNames from '../../constant/CollectionNames';
import { InstanceContainer } from '../../config/binding/InstanceBinding';
import Injectables from '../../constant/Injectables';
import { CheckerRequest } from '../../domain/maker-checker/MakerCheckerDomains';
import eq = query.eq;
import and = query.and;
import AnimatedButton from './AnimatedButton';
import ComponentForm from './rule/ComponentForm';
import LanguageUtil from '../../util/LanguageUtil';

class CustomEditorItemComponent extends Component<Props, State> {
  public state: State = new State();

  private checkerRequestSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;

  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  public render(): ReactNode {
    let {
      name = '',
      description = '',
      component,
      onChangeName,
      onChangeDescription,
      onSave,
      onRemove,
      onDeploy,
      onUndeploy,
      disableSave,
      disableDeploy,
      disableUndeploy,
      disableRemove,
      disableNameChange,
      disableDescriptionChange,
      status,
      isUpdate,
      statusColor,
      descriptionVisible,
      statusVisible,
      icon,
      componentState: { selfLock },
    } = this.props;
    const { checkerRequest, loading } = this.state;
    const isCheckerRequest = checkerRequest && ['DEPLOY', 'UNDEPLOY', 'DELETE'].includes(checkerRequest.type);
    disableSave = disableSave || !selfLock;
    disableDeploy = loading || isCheckerRequest || disableDeploy || !selfLock || !isUpdate;
    disableUndeploy = loading || isCheckerRequest || disableUndeploy || !selfLock;
    disableRemove = loading || isCheckerRequest || disableRemove || !selfLock || !isUpdate;
    disableNameChange = disableNameChange || !selfLock;
    disableDescriptionChange = disableDescriptionChange || !selfLock;
    return (
      <div
        id='information-container'
        className={
          'flex select-none w-full px-5 py-5 font-custom justify-center gap-y-5 gap-x-3 lg:justify-start items-start lg:items-center  flex-col lg:flex-row font-formica'
        }
      >
        <div id='edit-section' className={'flex items-center justify-start w-full  lg:w-auto gap-x-3'}>
          <div
            id='icon'
            className={`${statusColor ? statusColor : 'bg-secondary/80'} rounded-full w-[60px] h-[60px] xl:w-[80px] xl:h-[80px] shrink-0 flex items-center justify-center`}
          >
            {icon && (
              <div id='icon' className={'text-f-purple'}>
                <Icon icon={icon} className={'hidden w-[26px] h-[26px] 2xl:w-[34px] 2xl:h-[34px]'} />
              </div>
            )}
          </div>
          <div id={'editable-fields'} className={'flex flex-col w-full'}>
            <div className={'flex items-center'}>
              <input
                disabled={isUpdate || disableNameChange}
                value={name || ''}
                onChange={(e) => onChangeName(e.currentTarget.value)}
                className={
                  'w-full  select-none text-[24px] 2xl:text-4xl bg-transparent border-none shadow-none hover:border-none focus:outline-none text-black'
                }
                onBlur={this.onBlurName}
                placeholder={LanguageUtil.get({ id: 'common.input.placeholder.name', defaultMessage: 'Name2' })}
                data-testid={`${component}_name_input`}
              />
            </div>
            <div className={'flex items-center gap-x-2'}>
              {(statusVisible == undefined || statusVisible) && <div className={'flex w-[100px] h-full'}>{this.getStatusComponent(status)}</div>}
              {(descriptionVisible == undefined || descriptionVisible) && (
                <div className={'flex items-center group relative'}>
                  <textarea
                    value={description || ''}
                    onChange={(e) => onChangeDescription(e.currentTarget.value)}
                    disabled={disableDescriptionChange}
                    className={
                      'w-[98%] h-[30px] min-w-[300px] bg-white custom-editor-component-description hover:border-b-[1px] hover:border-f-purple flex items-center justify-start py-1.5 text-start text-md focus:border-b-[1px] focus:border-f-purple resize-none focus:outline-none text-f-black-3'
                    }
                    placeholder={LanguageUtil.get({ id: 'common.input.placeholder.description', defaultMessage: 'Description' })}
                    data-testid={`${component}_description_input`}
                  />
                  <div
                    className={
                      'hidden group-focus-within:flex group-hover:flex absolute z-[1001] transition-all top-1/2 text-f-purple  transform -translate-x-1/2 -translate-y-1/2 -right-2 '
                    }
                  >
                    <Icon icon={'edit'} size={20}></Icon>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div id={'controller-container'} className={'flex  w-full lg:w-full items-end justify-end'}>
          {status &&
          ['ACTIVE', 'SCHEDULED', 'FAILED', 'EXPIRED', 'QUEUED', 'FETCHING', 'PREPROCESSING', 'TRAINING', 'VALIDATING', 'RESTORING', 'REBALANCING'].includes(
            status,
          ) ? (
            <div className={'flex items-center justify-end w-full gap-x-3 gap-y-5 flex-wrap lg:flex-nowrap !h-[24px]'}>
              {onSave && (
                <AnimatedButton
                  onClick={() => !disableSave && onSave && onSave()}
                  icon={'save'}
                  text={LanguageUtil.get({ id: 'common.button.save', defaultMessage: 'Save' })}
                  disabled={disableSave}
                  customType={'outlined'}
                  data-testid={`${component}_save_button`}
                />
              )}
              {onUndeploy && (
                <div className={'group relative'}>
                  <AnimatedButton
                    onClick={(e) => !disableUndeploy && onUndeploy && onUndeploy(e)}
                    icon={'undeploy'}
                    id='component_undeploy'
                    text={LanguageUtil.get({ id: 'common.button.undeploy', defaultMessage: 'Undeploy' })}
                    disabled={disableUndeploy}
                    customType={'secondary'}
                    data-testid={`${component}_undeploy_button`}
                  />
                  <span
                    className={`${isCheckerRequest && 'group-hover:flex'} hidden bg-black text-white rounded-lg px-2 py-2 items-center justify-center absolute top-10 text-xs right-0`}
                    data-testid={`${component}_undeploy_waiting_approval_text`}
                  >
                    {LanguageUtil.get({ id: 'common.label.waitingApproval', defaultMessage: 'Waiting for approval' })}
                  </span>
                </div>
              )}
              {onDeploy && (
                <div className={'group relative'}>
                  <AnimatedButton
                    onClick={(e) => !disableDeploy && onDeploy && onDeploy(e)}
                    icon={'deploy'}
                    text={LanguageUtil.get({ id: 'common.button.reDeploy', defaultMessage: 'Re-Deploy' })}
                    id='component_redeploy'
                    disabled={disableDeploy}
                    customType={'primary'}
                  />
                  <span
                    className={`${isCheckerRequest && 'group-hover:flex'} hidden bg-black text-white rounded-lg px-2 py-2 items-center justify-center absolute top-10 text-xs right-0`}
                    data-testid={`${component}_deploy_waiting_approval_text`}
                  >
                    {LanguageUtil.get({ id: 'common.label.waitingApproval', defaultMessage: 'Waiting for approval' })}
                  </span>
                </div>
              )}
            </div>
          ) : status == 'Empty' ? (
            <div className={'flex items-center justify-end w-full gap-x-3 gap-y-5 flex-wrap lg:flex-nowrap'}>
              {onSave && (
                <AnimatedButton
                  onClick={() => !disableSave && onSave?.()}
                  icon={'save'}
                  text={LanguageUtil.get({ id: 'common.button.save', defaultMessage: 'Save' })}
                  disabled={disableSave}
                  customType={'outlined'}
                  data-testid={`${component}_save_button`}
                />
              )}
              {onRemove && (
                <div className={'group relative'}>
                  <AnimatedButton
                    onClick={(e) => !disableRemove && onRemove && onRemove(e)}
                    icon='delete'
                    id='component_delete'
                    text={LanguageUtil.get({ id: 'common.button.remove', defaultMessage: 'Remove' })}
                    disabled={disableRemove}
                    customType={'secondary'}
                    data-testid={`${component}_remove_button`}
                  />
                  <span
                    className={`${isCheckerRequest && 'group-hover:flex'} hidden bg-black text-white rounded-lg px-2 py-2 items-center justify-center absolute top-10 text-xs right-0`}
                  >
                    {LanguageUtil.get({ id: 'common.label.waitingApproval', defaultMessage: 'Waiting for approval' })}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className={'flex items-center justify-end w-full gap-x-3 gap-y-5 flex-wrap lg:flex-nowrap'}>
              {onSave && (
                <AnimatedButton
                  onClick={() => !disableSave && onSave && onSave()}
                  icon={'save'}
                  text={LanguageUtil.get({ id: 'common.button.save', defaultMessage: 'Save' })}
                  disabled={disableSave}
                  customType={'outlined'}
                  data-testid={`${component}_save_button`}
                />
              )}
              {onRemove && (
                <div className={'group relative'}>
                  <AnimatedButton
                    onClick={(e) => !disableRemove && onRemove?.(e)}
                    icon={'delete'}
                    text={LanguageUtil.get({ id: 'common.button.remove', defaultMessage: 'Remove' })}
                    id='component_delete'
                    disabled={disableRemove}
                    customType={'secondary'}
                    data-testid={`${component}_remove_button`}
                  />
                  <span
                    className={`${isCheckerRequest && 'group-hover:flex'} hidden bg-black text-white rounded-lg px-2 py-2 items-center justify-center absolute top-10 text-xs right-0`}
                    data-testid={`${component}_remove_waiting_approval_text`}
                  >
                    {LanguageUtil.get({ id: 'common.label.waitingApproval', defaultMessage: 'Waiting for approval' })}
                  </span>
                </div>
              )}
              {onDeploy && (
                <div className={'group relative'}>
                  <AnimatedButton
                    onClick={(e) => !disableDeploy && onDeploy && onDeploy(e)}
                    icon={'deploy'}
                    text={LanguageUtil.get({ id: 'common.button.deploy', defaultMessage: 'Deploy' })}
                    id='component_deploy'
                    disabled={disableDeploy}
                    customType={'primary'}
                    data-testid={`${component}_deploy_button`}
                  />
                  <span
                    className={`${isCheckerRequest && 'group-hover:flex'} hidden bg-black text-white rounded-lg px-2 py-2 items-center justify-center absolute top-10 text-xs right-0`}
                    data-testid={`${component}_deploy_waiting_approval_text`}
                  >
                    {LanguageUtil.get({ id: 'common.label.waitingApproval', defaultMessage: 'Waiting for approval' })}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  public componentDidMount() {
    if (makerCheckerUsableComponents.includes(this.props.component) && this.props.isUpdate) {
      this.setState({ loading: true }, this.startTracker);
    }
  }

  public componentDidUpdate(prevProps: Props) {
    if (!prevProps.isUpdate && this.props.isUpdate) {
      this.resubscribeTracker();
    }
  }

  public componentWillUnmount() {
    this.stopTracker();
  }

  private startTracker = () => {
    const query = this.getQuery();
    this.checkerRequestSubscription = subscribe.Formica.subscribe(CollectionNames.CHECKER_REQUESTS, query);
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.checkerRequestSubscription]);
  };

  private resubscribeTracker = async () => {
    this.setState({ loading: true });
    if (this.computation == undefined) {
      this.startTracker();
    } else {
      this.computation.onStop(this.startTracker);
      await this.stopTracker();
    }
  };

  private tracker = () => {
    if (this.checkerRequestSubscription?.ready()) {
      this.buildData();
    }
  };

  private stopTracker = async () => {
    await this.checkerRequestSubscription?.stop();
    this.computation?.stop();
  };

  private buildData = async () => {
    const checkerRequest = await this.repository.find<CheckerRequest>(this.getQuery());
    this.setState({ checkerRequest, loading: false });
  };

  private getQuery = () => {
    const { component, name } = this.props;
    return query.QueryBuilder.select()
      .from(CollectionNames.CHECKER_REQUESTS)
      .where(and(eq('component', component), eq('documentId', name), eq('status', 'Waiting')))
      .select('status', 'username', 'type', 'component', 'documentId', 'lastUpdated')
      .orderBy({ field: 'lastUpdated', operator: 'DESC' })
      .limit(1)
      .build();
  };

  private getStatusComponent(status?: DeploymentStatus) {
    return <ItemStatusComponent status={status} />;
  }

  private onBlurName = (e: React.FocusEvent<HTMLInputElement>) => {
    const { form } = this.props;
    const value = e.target.value;
    if (form) {
      form.getField('name').onChange(value);
    }
  };
}

class State {
  checkerRequest: CheckerRequest | undefined;
  loading: boolean = false;
}

interface Props extends ReduxProps {
  name: string;
  component: ComponentName;
  description: string;
  icon?: string;
  form?: ComponentForm<any>;
  isUpdate: boolean;
  status?: DeploymentStatus;
  statusColor?: string;
  onChangeName: (e: string) => void;
  onChangeDescription: (e: string) => void;
  onSave?: () => void;
  onDeploy?: (e: any) => void;
  onUndeploy?: (e: any) => void;
  onRemove?: (e: any) => void;
  disableSave?: boolean;
  disableDeploy?: boolean;
  disableUndeploy?: boolean;
  disableRemove?: boolean;
  disableNameChange?: boolean;
  disableDescriptionChange?: boolean;
  descriptionVisible?: boolean;
  statusVisible?: boolean;
}

const makerCheckerUsableComponents = [
  ComponentName.TRANSACTION,
  ComponentName.STREAM,
  ComponentName.ACTION,
  ComponentName.AI_MODEL,
  ComponentName.LIST,
  ComponentName.CONNECTION,
  ComponentName.ENRICHMENT,
  ComponentName.QUERY_VARIABLE,
  ComponentName.LIST_VARIABLE,
  ComponentName.BASIC_VARIABLE,
  ComponentName.VELOCITY,
  ComponentName.API_VARIABLE,
  ComponentName.TRANSACTION,
];

const mapStateToProps = (state: RootState) => ({
  componentState: state.component as ComponentState,
});

const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(CustomEditorItemComponent);
